/**
 * Game Pick Card
 *
 * Card displaying game picks (spread, total, moneyline) with details:
 * - Pick type badge
 * - Teams and pick side
 * - Confidence score
 * - Risk level
 * - Vegas edge
 * - Reasoning
 */

import React from 'react';

// Sport badge backgrounds
const SPORT_BG = {
  nba: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  nhl: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  nfl: 'bg-green-500/20 text-green-400 border-green-500/30',
  mlb: 'bg-red-500/20 text-red-400 border-red-500/30',
};

// Pick type labels and colors
const PICK_TYPE_CONFIG = {
  game_spread: { label: 'SPREAD', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  game_total: { label: 'TOTAL', className: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  game_ml: { label: 'MONEYLINE', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
};

// Get risk level styling
function getRiskStyle(risk) {
  if (!risk) return null;
  const r = risk.toLowerCase();
  if (r === 'low') return { className: 'bg-green-500/20 text-green-400 border-green-500/30' };
  if (r === 'elevated') return { className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
  if (r === 'high') return { className: 'bg-red-500/20 text-red-400 border-red-500/30' };
  return { className: 'bg-gray-500/20 text-gray-400 border-gray-500/30' };
}

// Confidence ring component
function ConfidenceRing({ value, size = 48 }) {
  // composite_score is already 0-100 scale, don't multiply
  const percentage = Math.round(value || 0);
  const radius = (size - 6) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Color based on confidence
  const color = percentage >= 70 ? '#22c55e' : percentage >= 50 ? '#eab308' : '#ef4444';

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255,255,255,0.1)"
          strokeWidth="3"
          fill="transparent"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth="3"
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-bold text-text-primary">{percentage}</span>
      </div>
    </div>
  );
}

export default function GamePickCard({ pick, featured = false }) {
  const {
    sport,
    pick_type,
    home_team,
    away_team,
    home_team_logo,
    away_team_logo,
    pick_side,
    line,
    composite_score,
    ml_confidence,
    vegas_edge,
    risk_level,
    reasoning,
  } = pick;

  const sportBg = SPORT_BG[sport] || SPORT_BG.nba;
  const pickTypeConfig = PICK_TYPE_CONFIG[pick_type] || { label: pick_type, className: 'bg-gray-500/20 text-gray-400' };
  const riskStyle = getRiskStyle(risk_level);

  // Use home_team and away_team directly from API
  const awayTeam = away_team || 'Away';
  const homeTeam = home_team || 'Home';

  // Format pick display based on type
  let pickDisplay = '';
  if (pick_type === 'game_spread') {
    pickDisplay = `${pick_side} ${line > 0 ? '+' : ''}${line}`;
  } else if (pick_type === 'game_total') {
    // Game totals use pick_side (over/under), not pick_direction
    pickDisplay = `${pick_side?.toUpperCase() || 'OVER'} ${line}`;
  } else if (pick_type === 'game_ml') {
    pickDisplay = pick_side;
  }

  return (
    <div
      className={`glass-card p-4 transition-all hover:border-accent-blue/50 ${
        featured ? 'border-2 border-yellow-500/50 ring-1 ring-yellow-500/20' : ''
      }`}
    >
      {/* Header Row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {/* Sport Badge */}
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${sportBg}`}>
            {sport?.toUpperCase()}
          </span>
          {/* Pick Type Badge */}
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${pickTypeConfig.className}`}>
            {pickTypeConfig.label}
          </span>
          {/* Featured Badge */}
          {featured && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gradient-to-r from-yellow-500 to-amber-500 text-black">
              &#9733; FEATURED
            </span>
          )}
        </div>
        {/* Confidence Ring */}
        <ConfidenceRing value={composite_score} />
      </div>

      {/* Matchup Display - Logo on top, team name below */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          {/* Away Team */}
          <div className="flex flex-col items-center flex-1">
            {away_team_logo ? (
              <img src={away_team_logo} alt={awayTeam} className="w-12 h-12 object-contain mb-1" />
            ) : (
              <div className="w-12 h-12 bg-bg-card rounded-full flex items-center justify-center mb-1">
                <span className="text-lg font-bold text-text-muted">{awayTeam?.charAt(0)}</span>
              </div>
            )}
            <span className="text-xs text-text-secondary text-center truncate max-w-[80px]">{awayTeam}</span>
          </div>

          {/* @ Symbol */}
          <div className="px-2">
            <span className="text-text-muted text-sm">@</span>
          </div>

          {/* Home Team */}
          <div className="flex flex-col items-center flex-1">
            {home_team_logo ? (
              <img src={home_team_logo} alt={homeTeam} className="w-12 h-12 object-contain mb-1" />
            ) : (
              <div className="w-12 h-12 bg-bg-card rounded-full flex items-center justify-center mb-1">
                <span className="text-lg font-bold text-text-muted">{homeTeam?.charAt(0)}</span>
              </div>
            )}
            <span className="text-xs text-text-secondary text-center truncate max-w-[80px]">{homeTeam}</span>
          </div>
        </div>
      </div>

      {/* Pick Display */}
      <div className="mb-4 p-3 rounded-lg bg-bg-card">
        <div className="text-center">
          <span className="text-xl font-black text-accent-blue">
            {pickDisplay}
          </span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {/* ML Confidence */}
        <div className="bg-bg-card p-2 rounded-lg text-center">
          <div className="text-xs text-text-secondary mb-1">ML Conf</div>
          <div className="text-sm font-bold text-text-primary">
            {ml_confidence != null
              ? `${Math.round(
                  ml_confidence > 10 ? ml_confidence :           // Already 0-100
                  ml_confidence > 1 ? ml_confidence * 10 :       // 1-10 scale → multiply by 10
                  ml_confidence * 100                             // 0-1 scale → multiply by 100
                )}%`
              : '-'}
          </div>
        </div>
        {/* Vegas Edge */}
        <div className="bg-bg-card p-2 rounded-lg text-center">
          <div className="text-xs text-text-secondary mb-1">Edge</div>
          <div className={`text-sm font-bold ${vegas_edge > 0 ? 'text-green-400' : vegas_edge < 0 ? 'text-red-400' : 'text-text-primary'}`}>
            {vegas_edge ? `${vegas_edge > 0 ? '+' : ''}${vegas_edge.toFixed(1)}` : '-'}
          </div>
        </div>
      </div>

      {/* Risk Level */}
      {riskStyle && (
        <div className="mb-3">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${riskStyle.className}`}>
            {risk_level?.toUpperCase()} RISK
          </span>
        </div>
      )}

      {/* Reasoning */}
      {reasoning && (
        <div className="pt-3 border-t border-border">
          <p className="text-xs text-text-secondary leading-relaxed line-clamp-3">
            {reasoning}
          </p>
        </div>
      )}
    </div>
  );
}
